import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from 'react';
import { SessionState } from 'sip.js';
import { PhoneIncoming, PhoneOutgoing } from 'lucide-react';
import { Tooltip } from 'primereact/tooltip';
import { CallControlIcon } from '@/components/call-control/CallControlIcons';
import { useSipCall } from '@/shared/providers/SipCallProvider';
import AgentStatisticsDialog from '@/pages/dialogs/AgentStatisticsDialog';
import BreakRequestDialog from '@/pages/dialogs/BreakRequestDialog';
import ReturnToServiceDialog from '@/pages/dialogs/ReturnToServiceDialog';
import { useToastContext } from '@/layout/context/toast-context';
import { useBackendPhonePause } from '@/shared/hooks/useBackendPhonePause';
import { clearActiveBreak, setActiveBreak } from '@/shared/auth/auth.reducer';
import { getAccessToken } from '@/shared/auth/jwt-storage';
import { parseBreakStartMs } from '@/shared/model/break.model';
import { useAppDispatch, useAppSelector } from '@/store';
import { createBreakRequest, endBreak } from '@/services/break/breakApi';
import { clearPhonePause, fetchPhonePauseStatus, pauseIncomingCallsOutgoingOnly, } from '@/services/phone-pause/phonePauseApi';
import { resolveIncomingCallsPaused } from '@/shared/model/phone-pause';
import GreetingUploadDialog from '@/pages/dialogs/GreetingUploadDialog';
import SoftphoneModal from '@/pages/softphone/SoftphoneModal';
import TransferCallDialog from '@/pages/dialogs/TransferCallDialog';
import { useHasAnyAuthority } from '@/shared/auth/use-has-any-authority';
import { APP_FEATURE_GUARDS } from '@/shared/constants/app-permissions';
const ACTIONS = [
    { id: 'answer', label: 'Answer call' },
    { id: 'hangup', label: 'Hang up' },
    { id: 'forward', label: 'Forward call' },
    { id: 'mute', label: 'Mute microphone' },
    { id: 'hold', label: 'Hold call' },
    { id: 'phone', label: 'Open softphone' },
    { id: 'greeting', label: 'Upload greeting audio' }
];
const CALL_TOOLBAR_SVG_COMMON = {
    viewBox: '0 0 1024 1024',
    xmlns: 'http://www.w3.org/2000/svg',
    fill: 'currentColor',
    focusable: false,
    'aria-hidden': true,
    width: '1em',
    height: '1em'
};
function CallIcon({ action, micMuted, callHeld, }) {
    switch (action) {
        case 'answer':
            return _jsx(CallControlIcon, { glyph: "answer" });
        case 'hangup':
            return _jsx(CallControlIcon, { glyph: "hangup" });
        case 'forward':
            return (_jsx("svg", { ...CALL_TOOLBAR_SVG_COMMON, "data-icon": "call-forward", children: _jsx("path", { d: "M370.4 228.6c34 34 47.2 66.8 29.4 93.1l-46.9 69.5c-13.8 20.4-11.1 47.6 6.4 65.1l208.4 208.4c17.5 17.5 44.7 20.2 65.1 6.4l69.5-46.9c26.3-17.8 59.1-4.6 93.1 29.4l46.8 46.8c25.8 25.8 25.8 67.6 0 93.4l-30.5 30.5c-38.6 38.6-95 53.8-151.1 40.9-103.5-23.8-208.2-85.2-300-177S207.4 491.7 183.6 388.2c-12.9-56.1 2.3-112.5 40.9-151.1l30.5-30.5c25.8-25.8 67.6-25.8 93.4 0l22 22zM670.4 247.2l147.5 147.5H517.3c-18.2 0-33 14.8-33 33s14.8 33 33 33h300.6L670.4 608.2c-12.9 12.9-12.9 33.8 0 46.7s33.8 12.9 46.7 0l204-204c12.9-12.9 12.9-33.8 0-46.7l-204-204c-12.9-12.9-33.8-12.9-46.7 0s-12.9 33.8 0 47z" }) }));
        case 'mute':
            return (_jsx(CallControlIcon, { glyph: "mute", className: micMuted ? 'layout-call-toolbar__glyph--mic-muted-on' : undefined }));
        case 'hold':
            return (_jsx(CallControlIcon, { glyph: "hold", className: callHeld ? 'layout-call-toolbar__glyph--call-held-on' : undefined }));
        case 'phone':
            return (_jsxs("svg", { width: "24", height: "24", viewBox: "0 0 24 24", fill: "currentColor", children: [_jsx("circle", { cx: "6", cy: "5", r: "1.7" }), _jsx("circle", { cx: "12", cy: "5", r: "1.7" }), _jsx("circle", { cx: "18", cy: "5", r: "1.7" }), _jsx("circle", { cx: "6", cy: "11", r: "1.7" }), _jsx("circle", { cx: "12", cy: "11", r: "1.7" }), _jsx("circle", { cx: "18", cy: "11", r: "1.7" }), _jsx("circle", { cx: "6", cy: "17", r: "1.7" }), _jsx("circle", { cx: "12", cy: "17", r: "1.7" }), _jsx("circle", { cx: "18", cy: "17", r: "1.7" }), _jsx("circle", { cx: "12", cy: "22", r: "1.7" })] }));
        case 'greeting':
            return (_jsx("svg", { ...CALL_TOOLBAR_SVG_COMMON, "data-icon": "greeting-record", children: _jsx("path", { d: "M512 140c115.9 0 210 94.1 210 210v92c0 115.9-94.1 210-210 210S302 557.9 302 442v-92c0-115.9 94.1-210 210-210zm0 64c-80.6 0-146 65.4-146 146v92c0 80.6 65.4 146 146 146s146-65.4 146-146v-92c0-80.6-65.4-146-146-146zm286 238c17.7 0 32 14.3 32 32 0 164.8-123.2 300.9-282 321.3V872h96c17.7 0 32 14.3 32 32s-14.3 32-32 32H380c-17.7 0-32-14.3-32-32s14.3-32 32-32h96v-76.7C317.2 774.9 194 638.8 194 474c0-17.7 14.3-32 32-32s32 14.3 32 32c0 140.3 113.7 254 254 254s254-113.7 254-254c0-17.7 14.3-32 32-32zm-30-270a70 70 0 110 140 70 70 0 010-140z" }) }));
        default:
            return null;
    }
}
function pad2(n) {
    return String(n).padStart(2, '0');
}
/** MM:SS — used for signed-in, idle, break */
function formatMmSs(totalSeconds) {
    const m = Math.floor(totalSeconds / 60);
    const s = totalSeconds % 60;
    return `${pad2(m)}:${pad2(s)}`;
}
/** Elapsed MM:SS — shared for ringing, idle, talking, etc. */
function elapsedMmSs(msStart, nowMs) {
    return Math.max(0, Math.floor((nowMs - msStart) / 1000));
}
/** Primary / secondary lines + badge tone from SIP legs + CRM agent phase. */
function deriveToolbarCallBanner(opts) {
    const ui = opts.conversation;
    const inboundRinging = opts.inboundRingCaller !== null;
    const outboundPreAnswer = ui?.direction === 'outbound' &&
        (ui.sipState === SessionState.Initial ||
            ui.sipState === SessionState.Establishing ||
            (ui.sipState === SessionState.Terminating && ui.outboundPhase === 'line-busy'));
    const inboundPreEstablish = ui?.direction === 'inbound' &&
        !inboundRinging &&
        (ui.sipState === SessionState.Initial || ui.sipState === SessionState.Establishing);
    if (inboundRinging) {
        const ringMs = opts.inboundRingStartedMs ?? opts.nowMs;
        const secs = elapsedMmSs(ringMs, opts.nowMs);
        return {
            tone: 'ringing',
            primary: `Ringing : ${formatMmSs(secs)}`,
            secondary: opts.inboundRingCaller ? `Incoming · ${opts.inboundRingCaller}` : 'Incoming call',
        };
    }
    if (outboundPreAnswer && ui) {
        const secs = elapsedMmSs(ui.callStartedMs, opts.nowMs);
        if (ui.outboundPhase === 'line-busy') {
            return {
                tone: 'connecting',
                primary: `Ringing : ${formatMmSs(secs)}`,
                secondary: ui.remoteLabel ? `Calling · ${ui.remoteLabel}` : 'Outbound call',
            };
        }
        return {
            tone: 'ringing',
            primary: `Ringing : ${formatMmSs(secs)}`,
            secondary: ui.remoteLabel ? `Calling · ${ui.remoteLabel}` : 'Outbound call',
        };
    }
    if (inboundPreEstablish && ui) {
        const secs = elapsedMmSs(ui.callStartedMs, opts.nowMs);
        return {
            tone: 'connecting',
            primary: `Connecting : ${formatMmSs(secs)}`,
            secondary: ui.remoteLabel ? `Inbound · ${ui.remoteLabel}` : 'Inbound call',
        };
    }
    if (ui &&
        (ui.sipState === SessionState.Established || ui.sipState === SessionState.Terminating)) {
        const base = ui.answeredAtMs ?? ui.callStartedMs;
        const secs = elapsedMmSs(base, opts.nowMs);
        const dirWord = ui.direction === 'inbound' ? 'Inbound' : 'Outbound';
        const remote = ui.remoteLabel ? `${dirWord} · ${ui.remoteLabel}` : `${dirWord} call`;
        return {
            tone: 'in-call',
            primary: `Talking : ${formatMmSs(secs)}`,
            secondary: remote,
        };
    }
    if (opts.agentPhase === 'acw' && (opts.acwRemainingSeconds ?? 0) > 0) {
        const remaining = opts.acwRemainingSeconds != null && opts.acwRemainingSeconds > 0
            ? ` · ${formatMmSs(opts.acwRemainingSeconds)} until ready`
            : '';
        return {
            tone: 'acw',
            primary: `ACW : ${formatMmSs(opts.crmElapsedSeconds)}`,
            secondary: `After-call work — not receiving calls${remaining}`,
        };
    }
    if (opts.agentPhase === 'break') {
        const suffix = opts.breakReasonSummary?.trim()
            ? ` · ${opts.breakReasonSummary}`
            : '';
        return {
            tone: 'break',
            primary: `On break : ${formatMmSs(opts.crmElapsedSeconds)}`,
            secondary: `Not receiving queue calls${suffix}`,
        };
    }
    if (opts.incomingCallsPaused &&
        !inboundRinging &&
        !ui &&
        opts.agentPhase !== 'acw') {
        const secs = opts.agentPhase === 'signed-in' && opts.sipRegistered
            ? elapsedMmSs(opts.idleStartMs, opts.nowMs)
            : opts.crmElapsedSeconds;
        return {
            tone: 'idle',
            primary: `Outgoing only : ${formatMmSs(secs)}`,
            secondary: 'Not receiving inbound queue calls',
        };
    }
    if (opts.agentPhase === 'idle') {
        return {
            tone: 'idle',
            primary: `Idle : ${formatMmSs(opts.crmElapsedSeconds)}`,
            secondary: 'Waiting for calls',
        };
    }
    /** Registered SIP + desk idle — clock how long queue wait lasts between sessions. */
    if (opts.agentPhase === 'signed-in' &&
        opts.sipRegistered &&
        ui === null &&
        opts.inboundRingCaller === null) {
        const secs = elapsedMmSs(opts.idleStartMs, opts.nowMs);
        return {
            tone: 'idle',
            primary: `Idle : ${formatMmSs(secs)}`,
            secondary: 'Waiting for calls',
        };
    }
    if (opts.agentPhase === 'in-call') {
        return {
            tone: 'in-call',
            primary: `Talking : ${formatMmSs(opts.crmElapsedSeconds)}`,
            secondary: 'Inbound Call',
        };
    }
    return {
        tone: 'signed-in',
        primary: `Signed in : ${formatMmSs(opts.crmElapsedSeconds)}`,
        secondary: 'Connected — agent profile',
    };
}
function initialsFromDisplayName(name) {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0)
        return '?';
    if (parts.length === 1)
        return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}
function BreakApplyToolbarIcon() {
    return (_jsx(_Fragment, { children: _jsx("svg", { ...CALL_TOOLBAR_SVG_COMMON, "data-icon": "break-apply", children: _jsx("path", { d: "M192 256c0-35.3 28.7-64 64-64h448c35.3 0 64 28.7 64 64v256c0 106-86 192-192 192H384c-106 0-192-86-192-192V256zm576 64v64h32c35.3 0 64 28.7 64 64s-28.7 64-64 64h-32c0 35.8-7.8 69.8-21.8 100.4H800c88.4 0 160-71.6 160-160S888.4 320 800 320h-32zM288 800h448c26.5 0 48 21.5 48 48s-21.5 48-48 48H288c-26.5 0-48-21.5-48-48s21.5-48 48-48zM352 96c17.7 0 32 14.3 32 32v32c0 17.7-14.3 32-32 32s-32-14.3-32-32v-32c0-17.7 14.3-32 32-32zm160 0c17.7 0 32 14.3 32 32v32c0 17.7-14.3 32-32 32s-32-14.3-32-32v-32c0-17.7 14.3-32 32-32zm160 0c17.7 0 32 14.3 32 32v32c0 17.7-14.3 32-32 32s-32-14.3-32-32v-32c0-17.7 14.3-32 32-32z" }) }) }));
}
/** Inline PrimeIcon for “Return to service” — matches Statistics button (`pi-chart-bar`). */
function ServicePrimeIcon() {
    return (_jsx("span", { className: "layout-call-toolbar__icon layout-call-toolbar__icon--prime", children: _jsx("i", { className: "pi pi-headphones", "aria-hidden": true, "data-icon": "return-to-service" }) }));
}
/** End ACW / ready for calls — icon-only like Statistics. */
function ReadyForCallsPrimeIcon() {
    return (_jsx("span", { className: "layout-call-toolbar__icon layout-call-toolbar__icon--prime", children: _jsx("i", { className: "pi pi-check-circle", "aria-hidden": true, "data-icon": "ready-for-calls" }) }));
}
/**
 * Secondary toolbar fixed under the main app topbar — visible on all authenticated layout pages.
 */
export default function AppCallToolbar({ agentPhase = 'signed-in', phaseStartedAt, agentDisplayName, agentId, agentLanguageLine = 'LANGUAGE: DARI (VIP)', agentInitials, agentOnline = true, }) {
    const { isOnBackendAcw, acwRemainingSeconds, acwElapsedSeconds, endLocalAcw } = useBackendPhonePause();
    const crmProfile = useAppSelector((state) => state.auth.crmProfile);
    const session = useAppSelector((state) => state.auth.session);
    const resolvedDisplayName = agentDisplayName?.trim() ||
        crmProfile?.name?.trim() ||
        [session?.account?.firstName, session?.account?.lastName].filter(Boolean).join(' ').trim() ||
        session?.username ||
        '—';
    const resolvedAgentId = agentId != null && String(agentId).trim() !== ''
        ? agentId
        : crmProfile?.phoneUser?.trim() || '—';
    const dispatch = useAppDispatch();
    const activeBreak = useAppSelector((state) => state.auth.activeBreak);
    const supervisorForceServiceRevision = useAppSelector((state) => state.auth.supervisorForceServiceRevision);
    const lastSupervisorForceServiceCleared = useAppSelector((state) => state.auth.lastSupervisorForceServiceCleared);
    const sip = useSipCall();
    const { showToast } = useToastContext();
    const { incomingRing, conversationUi, answerIncoming, hangupActiveOrDeclineIncoming, toggleMute, toggleHold, activeCallMuted, activeCallHeld, } = sip;
    const canAnswer = incomingRing !== null;
    const hasLiveConversation = conversationUi !== null && conversationUi.sipState !== SessionState.Terminated;
    const canHangup = incomingRing !== null || hasLiveConversation;
    const canMuteHold = conversationUi?.sipState === SessionState.Established;
    const canForward = canMuteHold;
    const idleStartMsRef = useRef(Date.now());
    const wasSipBusyRef = useRef(false);
    const wasOnBackendAcwRef = useRef(false);
    const [inboundRingStartedMs, setInboundRingStartedMs] = useState(null);
    /** Wall-clock anchor for unanswered inbound INVITE timers. */
    useEffect(() => {
        if (sip.incomingRing !== null)
            setInboundRingStartedMs((prev) => (prev === null ? Date.now() : prev));
        else
            setInboundRingStartedMs(null);
    }, [sip.incomingRing]);
    /** Restart SIP “idle wait” clock after any ring or session ends. */
    useEffect(() => {
        const busy = sip.incomingRing !== null || sip.conversationUi !== null;
        if (busy)
            wasSipBusyRef.current = true;
        else if (wasSipBusyRef.current) {
            idleStartMsRef.current = Date.now();
            wasSipBusyRef.current = false;
        }
    }, [sip.incomingRing, sip.conversationUi]);
    /** When backend ACW countdown finishes, restart idle wait clock from now. */
    useEffect(() => {
        if (wasOnBackendAcwRef.current && !isOnBackendAcw) {
            idleStartMsRef.current = Date.now();
        }
        wasOnBackendAcwRef.current = isOnBackendAcw;
    }, [isOnBackendAcw]);
    const [fallbackStart] = useState(() => Date.now());
    const effectiveStart = phaseStartedAt ?? fallbackStart;
    const [elapsedSeconds, setElapsedSeconds] = useState(() => Math.max(0, Math.floor((Date.now() - effectiveStart) / 1000)));
    useEffect(() => {
        const compute = () => Math.max(0, Math.floor((Date.now() - effectiveStart) / 1000));
        setElapsedSeconds(compute());
        const id = window.setInterval(() => setElapsedSeconds(compute()), 1000);
        return () => window.clearInterval(id);
    }, [effectiveStart]);
    const breakAnchorMs = activeBreak
        ? (parseBreakStartMs(activeBreak.breakStartTime) ?? Date.now())
        : null;
    const [breakElapsedSeconds, setBreakElapsedSeconds] = useState(0);
    useEffect(() => {
        if (breakAnchorMs === null) {
            setBreakElapsedSeconds(0);
            return;
        }
        const compute = () => Math.max(0, Math.floor((Date.now() - breakAnchorMs) / 1000));
        setBreakElapsedSeconds(compute());
        const id = window.setInterval(() => setBreakElapsedSeconds(compute()), 1000);
        return () => window.clearInterval(id);
    }, [breakAnchorMs]);
    const conversation = sip.conversationUi !== null
        ? {
            direction: sip.conversationUi.direction,
            remoteLabel: sip.conversationUi.remoteLabel,
            sipState: sip.conversationUi.sipState,
            callStartedMs: sip.conversationUi.callStartedMs,
            answeredAtMs: sip.conversationUi.answeredAtMs,
            outboundPhase: sip.conversationUi.outboundPhase,
        }
        : null;
    const isOnManualBreak = activeBreak != null;
    const showBackendAcw = isOnBackendAcw && acwRemainingSeconds > 0;
    const toolbarAgentPhase = isOnManualBreak
        ? 'break'
        : showBackendAcw
            ? 'acw'
            : agentPhase;
    const crmElapsedForBanner = toolbarAgentPhase === 'break'
        ? breakElapsedSeconds
        : toolbarAgentPhase === 'acw'
            ? acwElapsedSeconds
            : elapsedSeconds;
    const [statsDialogVisible, setStatsDialogVisible] = useState(false);
    const [breakDialogVisible, setBreakDialogVisible] = useState(false);
    const [serviceDialogVisible, setServiceDialogVisible] = useState(false);
    const [greetingDialogVisible, setGreetingDialogVisible] = useState(false);
    const [softphoneVisible, setSoftphoneVisible] = useState(false);
    const [forwardDialogVisible, setForwardDialogVisible] = useState(false);
    const [incomingCallsPaused, setIncomingCallsPaused] = useState(false);
    const [incomingPauseLoading, setIncomingPauseLoading] = useState(false);
    const syncIncomingPauseFromServer = useCallback(() => {
        void fetchPhonePauseStatus()
            .then((status) => setIncomingCallsPaused(resolveIncomingCallsPaused(status)))
            .catch(() => undefined);
    }, []);
    /** Restore manual inbound-pause toggle after login or hard refresh (`GET /api/phone-pause/status`). */
    useEffect(() => {
        const hasToken = Boolean(getAccessToken());
        if (!session && !hasToken)
            return;
        syncIncomingPauseFromServer();
        const onPageShow = () => {
            syncIncomingPauseFromServer();
        };
        window.addEventListener('pageshow', onPageShow);
        return () => window.removeEventListener('pageshow', onPageShow);
    }, [session, syncIncomingPauseFromServer]);
    /** Supervisor force-service websocket — sync toolbar pause state from clearedPauseTypes. */
    useEffect(() => {
        if (supervisorForceServiceRevision <= 0)
            return;
        if (lastSupervisorForceServiceCleared.includes('BREAK')) {
            setBreakDialogVisible(false);
            setServiceDialogVisible(false);
        }
        if (lastSupervisorForceServiceCleared.includes('ACW')) {
            endLocalAcw();
        }
        if (lastSupervisorForceServiceCleared.includes('OUTGOING_ONLY')) {
            setIncomingCallsPaused(false);
        }
        syncIncomingPauseFromServer();
    }, [
        supervisorForceServiceRevision,
        lastSupervisorForceServiceCleared,
        endLocalAcw,
        syncIncomingPauseFromServer,
    ]);
    const banner = deriveToolbarCallBanner({
        nowMs: Date.now(),
        agentPhase: toolbarAgentPhase,
        crmElapsedSeconds: crmElapsedForBanner,
        sipRegistered: sip.sipStatus === 'registered',
        idleStartMs: idleStartMsRef.current,
        inboundRingCaller: sip.incomingRing?.callerLabel ?? null,
        inboundRingStartedMs,
        breakReasonSummary: isOnManualBreak ? activeBreak?.breakReason : undefined,
        acwRemainingSeconds: showBackendAcw ? acwRemainingSeconds : undefined,
        incomingCallsPaused: incomingCallsPaused && !showBackendAcw && !isOnManualBreak,
        conversation,
    });
    const avatarInitials = (agentInitials ?? initialsFromDisplayName(resolvedDisplayName)).slice(0, 3);
    const statsLabel = 'Statistics';
    const breakToolbarLabel = 'Apply for break';
    const serviceToolbarLabel = 'Return to service';
    const readyForCallsLabel = 'Ready for calls';
    const pauseIncomingLabel = 'Pause incoming calls';
    const resumeIncomingLabel = 'Resume incoming calls';
    const handleToolbarBreakServiceClick = () => {
        if (isOnManualBreak) {
            setServiceDialogVisible(true);
            return;
        }
        setBreakDialogVisible(true);
    };
    const handleEndAcw = async () => {
        try {
            const status = await clearPhonePause(null);
            endLocalAcw();
            const outgoingOnly = resolveIncomingCallsPaused(status);
            setIncomingCallsPaused(outgoingOnly);
            showToast({
                severity: 'success',
                summary: outgoingOnly ? 'Outgoing calls only' : 'Ready for calls',
                detail: outgoingOnly
                    ? 'After-call work ended. You are available for outbound calls only — inbound queue calls are paused.'
                    : 'After-call work ended. You are available for queue calls again.',
            });
        }
        catch (e) {
            const detail = e instanceof Error ? e.message : 'Could not end after-call work.';
            showToast({
                severity: 'error',
                summary: 'Could not end ACW',
                detail,
            });
            throw e;
        }
    };
    const handleToggleIncomingPause = async () => {
        if (incomingPauseLoading)
            return;
        setIncomingPauseLoading(true);
        try {
            if (incomingCallsPaused) {
                const status = await clearPhonePause('OUTGOING_ONLY');
                const stillPaused = resolveIncomingCallsPaused(status);
                setIncomingCallsPaused(stillPaused);
                showToast({
                    severity: 'success',
                    summary: stillPaused ? 'Outgoing calls only' : 'Incoming calls resumed',
                    detail: stillPaused
                        ? 'You are available for outbound calls only — inbound queue calls remain paused.'
                        : 'You will receive inbound queue calls again.',
                });
            }
            else {
                await pauseIncomingCallsOutgoingOnly();
                setIncomingCallsPaused(true);
                showToast({
                    severity: 'success',
                    summary: 'Incoming calls paused',
                    detail: 'Outbound calls only — you are not receiving inbound queue calls.',
                });
            }
        }
        catch (e) {
            const detail = e instanceof Error
                ? e.message
                : incomingCallsPaused
                    ? 'Could not resume incoming calls.'
                    : 'Could not pause incoming calls.';
            showToast({
                severity: 'error',
                summary: incomingCallsPaused ? 'Could not resume incoming calls' : 'Could not pause incoming calls',
                detail,
            });
        }
        finally {
            setIncomingPauseLoading(false);
        }
    };
    const handleResumeForService = async () => {
        try {
            await endBreak();
            dispatch(clearActiveBreak());
            showToast({
                severity: 'success',
                summary: 'Returned to service',
                detail: 'You will receive inbound queue calls again.',
            });
        }
        catch (e) {
            const detail = e instanceof Error ? e.message : 'Could not return to service.';
            showToast({
                severity: 'error',
                summary: 'Could not return to service',
                detail,
            });
            throw e;
        }
    };
    const agentStatistics = useHasAnyAuthority(APP_FEATURE_GUARDS.agentStatistics);
    const canPauseCall = useHasAnyAuthority(APP_FEATURE_GUARDS.pauseCall);
    return (_jsxs("nav", { className: "layout-call-toolbar", "aria-label": "Call controls", children: [_jsx(AgentStatisticsDialog, { visible: statsDialogVisible, onHide: () => setStatsDialogVisible(false) }), _jsx(BreakRequestDialog, { visible: breakDialogVisible, onHide: () => setBreakDialogVisible(false), onConfirmBreak: async ({ breakReason, estimatedBreakDuration }) => {
                    const breakRecord = await createBreakRequest({
                        breakReason,
                        estimatedBreakDuration,
                    });
                    dispatch(setActiveBreak(breakRecord));
                } }), _jsx(ReturnToServiceDialog, { visible: serviceDialogVisible, onHide: () => setServiceDialogVisible(false), onConfirmReturnToService: handleResumeForService }), _jsx(GreetingUploadDialog, { visible: greetingDialogVisible, onHide: () => setGreetingDialogVisible(false) }), _jsx(SoftphoneModal, { visible: softphoneVisible, onHide: () => setSoftphoneVisible(false) }), _jsx(TransferCallDialog, { visible: forwardDialogVisible, onHide: () => setForwardDialogVisible(false) }), _jsx(Tooltip, { target: ".layout-call-toolbar__tooltip-btn", position: "top", showDelay: 400 }), _jsx("div", { className: "layout-call-toolbar__agent", children: _jsxs("div", { className: "layout-call-toolbar__agent-card", children: [_jsxs("div", { className: "layout-call-toolbar__agent-avatar-wrap", children: [_jsx("div", { className: "layout-call-toolbar__agent-avatar", "aria-hidden": true, children: avatarInitials }), _jsx("span", { className: `layout-call-toolbar__agent-online-dot${agentOnline ? '' : ' layout-call-toolbar__agent-online-dot--offline'}`, "aria-label": agentOnline ? 'Online' : 'Offline' })] }), _jsxs("div", { className: "layout-call-toolbar__agent-content", children: [_jsxs("div", { className: "layout-call-toolbar__agent-title", children: [_jsx("span", { className: "layout-call-toolbar__agent-name", children: resolvedDisplayName }), ' ', _jsxs("span", { className: "layout-call-toolbar__agent-id", children: ["(ID: ", resolvedAgentId, ")"] })] }), _jsxs("div", { className: "layout-call-toolbar__agent-meta", children: [_jsx("span", { children: agentLanguageLine }), showBackendAcw ? (_jsxs("span", { className: "layout-call-toolbar__agent-acw-badge", children: ["ACW \u00B7 ", formatMmSs(acwRemainingSeconds), " left"] })) : null] })] })] }) }), _jsx("div", { className: "layout-call-toolbar__center", children: _jsx("ul", { className: "layout-call-toolbar__list", children: ACTIONS.map(({ id, label }) => {
                        const tooltipLabel = id === 'mute'
                            ? activeCallMuted
                                ? 'Unmute microphone'
                                : 'Mute microphone'
                            : id === 'hold'
                                ? activeCallHeld
                                    ? 'Resume call'
                                    : 'Hold call'
                                : label;
                        const sipControlDisabled = id === 'answer'
                            ? !canAnswer
                            : id === 'hangup'
                                ? !canHangup
                                : id === 'forward'
                                    ? !canForward
                                    : id === 'mute' || id === 'hold'
                                        ? !canMuteHold
                                        : false;
                        const handleClick = id === 'greeting'
                            ? () => setGreetingDialogVisible(true)
                            : id === 'phone'
                                ? () => setSoftphoneVisible(true)
                                : id === 'answer'
                                    ? () => void answerIncoming()
                                    : id === 'hangup'
                                        ? () => void hangupActiveOrDeclineIncoming()
                                        : id === 'forward'
                                            ? () => setForwardDialogVisible(true)
                                            : id === 'mute'
                                                ? () => toggleMute()
                                                : id === 'hold'
                                                    ? () => void toggleHold()
                                                    : undefined;
                        return (_jsx("li", { children: _jsx("span", { className: "layout-call-toolbar__ring", children: _jsx("button", { type: "button", className: "layout-call-toolbar__btn layout-call-toolbar__tooltip-btn p-link", "aria-label": tooltipLabel, "aria-haspopup": id === 'greeting' || id === 'phone' || id === 'forward'
                                        ? 'dialog'
                                        : undefined, "aria-expanded": id === 'greeting'
                                        ? greetingDialogVisible
                                        : id === 'phone'
                                            ? softphoneVisible
                                            : id === 'forward'
                                                ? forwardDialogVisible
                                                : undefined, "data-pr-tooltip": tooltipLabel, "data-pr-position": "top", disabled: sipControlDisabled, onClick: handleClick, children: _jsx("span", { className: "layout-call-toolbar__icon", children: _jsx(CallIcon, { action: id, micMuted: id === 'mute' ? activeCallMuted : undefined, callHeld: id === 'hold' ? activeCallHeld : undefined }) }) }) }) }, id));
                    }) }) }), _jsx("div", { className: "layout-call-toolbar__aside", children: _jsxs("div", { className: "layout-call-toolbar__aside-actions", children: [showBackendAcw ? (_jsx("span", { className: "layout-call-toolbar__ring layout-call-toolbar__ring--muted", children: _jsx("button", { type: "button", className: "layout-call-toolbar__btn layout-call-toolbar__tooltip-btn p-link", "aria-label": readyForCallsLabel, "data-pr-tooltip": readyForCallsLabel, "data-pr-position": "top", onClick: () => void handleEndAcw(), children: _jsx(ReadyForCallsPrimeIcon, {}) }) })) : null, _jsx("span", { className: "layout-call-toolbar__ring layout-call-toolbar__ring--muted", children: _jsx("button", { type: "button", className: "layout-call-toolbar__btn layout-call-toolbar__tooltip-btn p-link", "aria-label": isOnManualBreak ? serviceToolbarLabel : breakToolbarLabel, "aria-haspopup": "dialog", "aria-expanded": isOnManualBreak ? serviceDialogVisible : breakDialogVisible, "data-pr-tooltip": isOnManualBreak ? serviceToolbarLabel : breakToolbarLabel, "data-pr-position": "top", disabled: showBackendAcw, onClick: handleToolbarBreakServiceClick, children: isOnManualBreak ? (_jsx(ServicePrimeIcon, {})) : (_jsx("span", { className: "layout-call-toolbar__icon", children: _jsx(BreakApplyToolbarIcon, {}) })) }) }), canPauseCall ? (_jsx("span", { className: `layout-call-toolbar__ring layout-call-toolbar__ring--muted${incomingCallsPaused ? ' layout-call-toolbar__ring--incoming-paused' : ''}`, children: _jsx("button", { type: "button", className: `layout-call-toolbar__btn layout-call-toolbar__tooltip-btn p-link${incomingCallsPaused ? ' layout-call-toolbar__btn--incoming-paused' : ''}`, "aria-label": incomingCallsPaused ? resumeIncomingLabel : pauseIncomingLabel, "aria-pressed": incomingCallsPaused, "data-pr-tooltip": incomingCallsPaused
                                    ? resumeIncomingLabel
                                    : `${pauseIncomingLabel} (outgoing only)`, "data-pr-position": "top", disabled: showBackendAcw || isOnManualBreak || incomingPauseLoading, onClick: () => void handleToggleIncomingPause(), children: _jsx("span", { className: "layout-call-toolbar__icon layout-call-toolbar__icon--lucide", children: incomingCallsPaused ? (_jsx(PhoneOutgoing, { size: 18, "aria-hidden": true, "data-icon": "resume-incoming" })) : (_jsx(PhoneIncoming, { size: 18, "aria-hidden": true, "data-icon": "pause-incoming" })) }) }) })) : null, agentStatistics ? (_jsx("span", { className: "layout-call-toolbar__ring layout-call-toolbar__ring--muted", children: _jsx("button", { type: "button", className: "layout-call-toolbar__btn layout-call-toolbar__tooltip-btn p-link", "aria-label": statsLabel, "aria-haspopup": "dialog", "aria-expanded": statsDialogVisible, "data-pr-tooltip": statsLabel, "data-pr-position": "top", onClick: () => setStatsDialogVisible(true), children: _jsx("span", { className: "layout-call-toolbar__icon layout-call-toolbar__icon--prime", children: _jsx("i", { className: "pi pi-chart-bar", "aria-hidden": true }) }) }) })) : null, _jsxs("div", { className: `layout-call-toolbar__call-status layout-call-toolbar__call-status--${banner.tone}`, role: "status", "aria-live": "polite", "aria-atomic": "true", children: [_jsx("div", { className: "layout-call-toolbar__call-status-primary", children: banner.primary }), _jsx("div", { className: "layout-call-toolbar__call-status-secondary", children: banner.secondary })] })] }) })] }));
}
